import type { CaptureResult, PostHog, PostHogConfig } from "posthog-js";

const blockNoteDerivedEventNames = new Set([
  "$autocapture",
  "$copy_autocapture",
  "$dead_click",
  "$rageclick",
]);

const blockNoteSensitiveClasses = [
  "findin-blocknote-sensitive",
  "ph-no-capture",
];

// BlockNote's main DOM stays under the editor root. Its Radix/Floating UI
// menus can be portalled outside that root, but keep stable `bn-*` classes.
export const blockNoteSensitiveSelector = [
  ".findin-blocknote-sensitive",
  '[class^="bn-"]',
  '[class*=" bn-"]',
  ".bn-container",
  ".bn-toolbar",
  ".bn-side-menu",
  ".bn-menu-dropdown",
  ".bn-popover-content",
  ".bn-suggestion-menu",
  ".bn-grid-suggestion-menu",
  ".bn-file-panel",
].join(", ");

export const postHogReplayBlockSelector = [
  ".findin-workflow-document-sensitive",
  ".findin-paper-document-sensitive",
  ".findin-note-document-sensitive",
  blockNoteSensitiveSelector,
].join(", ");

export const postHogReplayTextMaskSelector = [
  postHogReplayBlockSelector,
  ".ph-sensitive",
  ".findin-workflow-content-sensitive",
  ".findin-workflow-bibliography-sensitive",
].join(", ");

const dynamicRoutePatterns: Array<{ pattern: RegExp; replacement: string }> = [
  { pattern: /^\/referrer\/[^/]+$/, replacement: "/referrer/:inviteCode" },
  { pattern: /^\/papers\/[^/]+$/, replacement: "/papers/:paperId" },
  { pattern: /^\/subjects\/[^/]+\/notes$/, replacement: "/subjects/:subjectId/notes" },
  { pattern: /^\/subjects\/[^/]+\/[^/]+$/, replacement: "/subjects/:subjectId/:paperId" },
  { pattern: /^\/subjects\/[^/]+$/, replacement: "/subjects/:subjectId" },
  {
    pattern: /^\/workflow-creator\/[^/]+\/[^/]+\/[^/]+$/,
    replacement: "/workflow-creator/:wfId/:summaryId/:step",
  },
  {
    pattern: /^\/workflow-creator\/[^/]+\/[^/]+$/,
    replacement: "/workflow-creator/:wfId/:summaryId",
  },
  { pattern: /^\/workflow-creator\/[^/]+$/, replacement: "/workflow-creator/:wfId" },
  { pattern: /^\/workflow\/[^/]+$/, replacement: "/workflow/:wfId" },
  { pattern: /^\/docs\/[^/]+$/, replacement: "/docs/:slug" },
  { pattern: /^\/t\/[^/]+$/, replacement: "/t/:shortId" },
];

let lastCapturedUrl: string | null = null;
let activeBlockNoteScopes = 0;
let scopedCaptureClient: PostHog | null = null;

function elementHasBlockNoteClass(value: unknown) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const element = value as Record<string, unknown>;
  const classes = Array.isArray(element.classes)
    ? element.classes.filter((className): className is string => typeof className === "string")
    : [];
  if (typeof element.attr__class === "string") {
    classes.push(...element.attr__class.split(/\s+/));
  }

  return classes.some((className) => (
    className.startsWith("bn-") || blockNoteSensitiveClasses.includes(className)
  ));
}

function isBlockNoteDerivedEvent(event: CaptureResult) {
  if (!blockNoteDerivedEventNames.has(event.event)) {
    return false;
  }

  const properties = event.properties as Record<string, unknown>;
  if (Array.isArray(properties.$elements) && properties.$elements.some(elementHasBlockNoteClass)) {
    return true;
  }

  const elementsChain = properties.$elements_chain;
  return typeof elementsChain === "string"
    && /(\.bn-|\.findin-blocknote-sensitive|\.ph-no-capture)/.test(elementsChain);
}

function syncScopedHeatmaps() {
  if (!scopedCaptureClient?.__loaded) {
    return;
  }
  scopedCaptureClient.set_config({ capture_heatmaps: activeBlockNoteScopes === 0 });
}

export function registerPostHogScopedCaptureClient(client: PostHog) {
  scopedCaptureClient = client;
  syncScopedHeatmaps();
}

export function enterPostHogBlockNoteScope(client?: PostHog) {
  activeBlockNoteScopes += 1;
  if (client?.__loaded) {
    scopedCaptureClient = client;
  }
  syncScopedHeatmaps();

  let exited = false;
  return () => {
    if (exited) {
      return;
    }
    exited = true;
    activeBlockNoteScopes = Math.max(0, activeBlockNoteScopes - 1);
    syncScopedHeatmaps();
  };
}

export function filterPostHogEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event || isBlockNoteDerivedEvent(event)) {
    return null;
  }
  return event;
}

export function sanitizePostHogPathname(pathname: string): string {
  const withoutQuery = pathname.split(/[?#]/, 1)[0] || "/";
  const normalized = withoutQuery.startsWith("/") ? withoutQuery : `/${withoutQuery}`;
  const trimmed = normalized.length > 1 ? normalized.replace(/\/+$/, "") : normalized;

  return dynamicRoutePatterns.find(({ pattern }) => pattern.test(trimmed))?.replacement ?? trimmed;
}

export function capturePostHogPageView(client: PostHog, origin: string, pathname: string) {
  const sanitizedPathname = sanitizePostHogPathname(pathname);
  const currentUrl = `${origin}${sanitizedPathname}`;
  if (currentUrl === lastCapturedUrl) {
    return;
  }

  const capturedEvent = client.capture("$pageview", {
    $current_url: currentUrl,
    $pathname: sanitizedPathname,
  });

  // Child effects can run before PostHogProvider's initialization effect. Do
  // not suppress the provider's loaded callback when that early capture was
  // ignored by the uninitialized client.
  if (capturedEvent) {
    lastCapturedUrl = currentUrl;
  }
}

export const postHogOptions: Partial<PostHogConfig> = {
  api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
  advanced_disable_flags: false,
  autocapture: {
    capture_copied_text: false,
    element_attribute_ignorelist: ["id", "data-id", "value"],
  },
  rageclick: true,
  capture_pageview: false,
  capture_pageleave: false,
  capture_heatmaps: true,
  capture_dead_clicks: false,
  capture_exceptions: false,
  capture_performance: false,
  enable_recording_console_log: false,
  disable_scroll_properties: true,
  disable_session_recording: false,
  disable_surveys: true,
  disable_surveys_automatic_display: true,
  disable_web_experiments: true,
  disable_external_dependency_loading: false,
  mask_all_text: false,
  mask_all_element_attributes: true,
  session_recording: {
    blockSelector: postHogReplayBlockSelector,
    maskTextSelector: postHogReplayTextMaskSelector,
    maskAllInputs: true,
    captureCanvas: {
      recordCanvas: false,
    },
    recordHeaders: false,
    recordBody: false,
    recordCrossOriginIframes: false,
  },
  person_profiles: "identified_only",
  save_campaign_params: false,
  save_referrer: false,
  before_send: filterPostHogEvent,
};
